"use strict";
/**
 * Capability-Based RBAC — central source of truth.
 *
 * Plan: ~/.claude/plans/review-of-this-hazy-marble.md (Wave 2 D3, 2026-04-29)
 * Audit CSV: /Users/dieterroylancem5/valutten-app/audit/2026-04-29-rbac-capability-mapping-reviewed.csv
 *
 * Why this exists:
 *   95 backend `enforceRole` sites + 67 inline `role === '...'` literals
 *   make role additions a 30+ file sweep where any miss silently
 *   regresses authorization for some role × surface combination. This
 *   matrix collapses every role-check to a capability check; adding a
 *   new role becomes a one-row matrix edit.
 *
 * Critical separation of concerns:
 *   - Capabilities answer "CAN this user call this endpoint?".
 *     They REPLACE `enforceRole` and `RequireRole` — nothing else.
 *   - Per-user data scope (hideCommissions, allowedReports,
 *     trailVisibilityMode, selfSourcedTagsEncoded, allowedEntityCodes,
 *     visiblePaymentTypes, linkedBrokerDocIds, assignedOffices) STAYS
 *     on user/broker docs and continues through resolveBrokerScope /
 *     resolveOfficeScope / resolveEntityScope / buildTrailVisibilityFilter.
 *   - Per-resource self-scope checks (`if (auth.uid !== payload.uid)`,
 *     `if (auth.role === 'broker' && brokerDocId !== auth.brokerDocId)`)
 *     STAY INLINE. The capability gate is the outer perimeter; the
 *     inline check is the actual security. Migration must not touch
 *     a single inline `if`.
 *
 * Matrix derivation:
 *   Mechanical mapping from audit CSV's `current_allow_set` column.
 *   Two-human reviewed (D1 generated, D2 devil's-advocate flagged
 *   risk patterns, decisions locked in conversation 2026-04-29).
 *   Behaviour byte-identical to existing enforceRole decisions for
 *   every role × site pair (verified by property test, prod-replay).
 *
 * Income Summary excluded:
 *   The Income Summary page is switched off indefinitely. None of its
 *   surfaces appear in the capability vocabulary below. Sites flagged
 *   DEAD_CODE_OR_DROP in the reviewed CSV are NOT migrated.
 *
 * Founder ↔ client app invariant:
 *   Per CLAUDE.md §7 "Admin Portal: founder only — Client App: founder
 *   does NOT exist". Founder gets EXACTLY one capability:
 *   `access_admin_portal`. NO automatic founder bypass in
 *   `enforceCapability`. NO short-circuit on the frontend
 *   `RequireCapability` for founder. The invariant is enforced by the
 *   compile-time type assertion at the bottom of this file AND the
 *   runtime test in `capabilities.test.ts`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ROLE_CAPABILITIES = void 0;
exports.hasCapability = hasCapability;
exports.hasAnyCapability = hasAnyCapability;
// ============================================================================
// Role → capability matrix
// ============================================================================
// Derived mechanically from `current_allow_set` in the reviewed audit CSV.
// Every entry traceable back to one or more S### site IDs. To audit:
//   git grep -n 'enforceCapability(ctx, .ROLE_CAP_GOES_HERE.)'
// matches the live call sites; cross-reference with audit CSV.
// Read-only baseline shared by broker, broker_assistant, AND credit_analyst.
// Write capabilities (e.g. tag_loans_broker_self) are NOT in this set and must
// be granted explicitly per role — credit_analyst is observational by design
// (CLAUDE.md §7), so this split keeps it from inheriting writes via spread.
const COMMON_BROKER_FAMILY_READS = [
    'view_own_dashboard',
    'view_settlement_age',
    'view_clawbacks',
    'view_loan_portfolio',
    'view_trail_drop_off',
    'view_book_health',
    'view_arrears',
    'view_discharged_loans',
    'view_book_churn',
    'view_refinance_intelligence',
    'view_acquired_books',
    'view_trail_leakage',
    'view_lead_inbox_self',
    'edit_leads_self',
    'accept_invitation',
    'complete_own_registration',
    'read_own_broker_commission_config',
    'manage_own_activity',
];
const ALL_REPORTS = [
    'view_settlement_age',
    'view_clawbacks',
    'view_loan_portfolio',
    'view_trail_drop_off',
    'view_trail_revenue_forecast',
    'view_trail_run_off',
    'view_trail_leakage',
    'view_book_health',
    'view_refinance_intelligence',
    'view_acquired_books',
    // NOTE: view_loan_books + view_acquisition_analysis are admin-tier-only
    // per CLAUDE.md §7 ("Loan Books → super_admin + administrator").
    // They are NOT in ALL_REPORTS so office_admin's spread does not include
    // them. They are added explicitly to ALL_CLIENT_APP_CAPS below so
    // super_admin and administrator retain access.
    'view_trail_book_valuation',
    'view_rcti_cashflow',
    'view_book_churn',
    'view_arrears',
    'view_discharged_loans',
];
const ALL_DASHBOARDS = [
    'view_company_dashboard',
    'view_office_dashboard',
    'view_commission_breakdown',
    'view_commission_amounts',
    'view_monthly_commissions',
];
const ALL_LEAD_OPS = [
    'view_lead_inbox_all',
    'view_lead_inbox_office',
    'view_lead_inbox_self',
    'edit_leads_all',
    'edit_leads_office',
    'edit_leads_self',
    'assign_leads',
    'configure_lead_routing',
    'manage_lead_taxonomy',
    'view_broker_activity',
];
const ALL_INVITATIONS = [
    'invite_brokers',
    'invite_broker_assistants',
    'invite_office_admins',
    'invite_credit_analysts',
    'invite_marketing',
];
const ALL_CLIENT_APP_CAPS = [
    ...ALL_DASHBOARDS,
    ...ALL_REPORTS,
    ...ALL_LEAD_OPS,
    ...ALL_INVITATIONS,
    'view_own_dashboard',
    'view_loan_books', // admin-tier only (CLAUDE.md §7)
    'view_acquisition_analysis', // admin-tier only (CLAUDE.md §7)
    'upload_commission_data',
    'manage_offices',
    'manage_brokers',
    'manage_team',
    'manage_billing',
    'manage_loan_books',
    'manage_broker_commission_config',
    'access_audit_log',
    'manage_integrations',
    'manage_tenant_settings',
    'manage_email_ingestion',
    'manage_connected_accounts',
    'read_any_broker_commission_config',
    'read_own_broker_commission_config',
    'manage_any_activity',
    'manage_own_activity',
    'tag_loans_admin',
    'tag_loans_broker_self',
    'delete_source_document',
    'accept_invitation',
    'complete_own_registration',
    'use_ai_assistant',
    'access_conversations_inbox',
];
exports.ROLE_CAPABILITIES = {
    // ── Founder: ADMIN PORTAL ONLY (CLAUDE.md §7) ────────────────────────────
    // Compile-time invariant at bottom of file enforces this. Do not add
    // any other capability to this row without removing the invariant
    // (which itself requires a separate audited PR).
    // `as const` narrows the tuple type so the type-level assertion at the
    // bottom of the file can check for the exact ['access_admin_portal']
    // shape rather than the wider readonly Capability[] inferred from the
    // outer Record annotation.
    founder: ['access_admin_portal'],
    // ── super_admin: every client-app cap ────────────────────────────────────
    super_admin: ALL_CLIENT_APP_CAPS,
    // ── administrator: every client-app cap EXCEPT billing ───────────────────
    // Per CLAUDE.md §7: "billing access: super_admin only".
    administrator: ALL_CLIENT_APP_CAPS.filter(c => c !== 'manage_billing'),
    // ── office_admin: scoped admin operations ────────────────────────────────
    // Cannot invite credit_analyst, broker_assistant, office_admin, marketing
    // (CLAUDE.md §7). Cannot manage billing, integrations, tenant settings,
    // loan books (admin-tier exclusives). No AI assistant.
    office_admin: [
        'view_company_dashboard',
        'view_office_dashboard',
        ...ALL_REPORTS,
        'view_lead_inbox_office',
        'edit_leads_office',
        'assign_leads',
        'view_broker_activity',
        'view_commission_breakdown',
        'view_commission_amounts',
        'view_monthly_commissions',
        'upload_commission_data',
        'manage_brokers',
        'manage_offices',
        'manage_team',
        'manage_connected_accounts',
        'invite_brokers',
        'read_any_broker_commission_config',
        'manage_any_activity',
        'tag_loans_admin',
        'delete_source_document',
        'accept_invitation',
        'complete_own_registration',
        'access_conversations_inbox',
    ],
    // ── credit_analyst: broker-family read access (linked-broker scope) ──────
    // Sees own data + linked brokers' data via resolveBrokerScope. Self-scope
    // by linkedBrokerDocIds claim. No write to other brokers' resources.
    // Per CLAUDE.md §7: "Loan Books → super_admin + administrator" — admin-tier
    // only, so credit_analyst does NOT receive view_loan_books or
    // view_acquisition_analysis (corrects 2026-05-05 over-grant; admit set now
    // matches CLAUDE.md and route guards at AppRoutes.tsx:248,253,265).
    // Observational by design: spreads READS only — does NOT inherit
    // tag_loans_broker_self or any other write capability via spread.
    credit_analyst: [
        ...COMMON_BROKER_FAMILY_READS,
        'manage_connected_accounts',
    ],
    // ── broker: own data only ────────────────────────────────────────────────
    // view_commission_breakdown is admin-tier-only per AppRoutes.tsx:216
    // ("Commission Breakdown: not available to brokers"). Broker sees own
    // commission data via view_commission_amounts + view_monthly_commissions
    // scoped to brokerDocId.
    broker: [
        ...COMMON_BROKER_FAMILY_READS,
        'tag_loans_broker_self',
        'view_commission_amounts',
        'view_monthly_commissions',
        'manage_connected_accounts',
        'access_conversations_inbox',
    ],
    // ── broker_assistant: parent broker's data via brokerDocId ───────────────
    // Same caps as broker; the data scope (resolveBrokerScope) reads the
    // PARENT's broker doc. view_commission_breakdown is admin-tier-only.
    // tag_loans_broker_self granted: assistant performs the tag on behalf of
    // the parent broker — the inline scope check on the tag handler verifies
    // brokerDocId matches the assistant's claim (which points at the parent).
    broker_assistant: [
        ...COMMON_BROKER_FAMILY_READS,
        'tag_loans_broker_self',
        'view_commission_amounts',
        'view_monthly_commissions',
        'manage_connected_accounts',
        'access_conversations_inbox',
    ],
    // ── marketing: CRM operator, no commission data by default ───────────────
    // Per CLAUDE.md §7: "NO commission-data scope by default ... Office-scoped
    // via assignedOffices". Brokers stricter than marketing on lead inbox
    // visibility (locked-in decision 2026-04-29): marketing sees full inbox,
    // office_admin sees office-scoped, brokers see assigned-to-self.
    //
    // Exclusions (2026-05-19):
    //   - view_trail_leakage: Broker Activity sub-page is broker-centric, not
    //     CRM. Marketing keeps view_broker_activity (Overview/Pipeline/
    //     Scorecard/Weekly Funnel still accessible).
    //   - manage_connected_accounts: Settings Connected Accounts OAuth is
    //     commission email-ingestion only; the marketing-facing lead-capture
    //     email connector lives under /leads.
    //   - use_ai_assistant: never granted (existing invariant).
    //   - view_lead_inbox_all / edit_leads_all: despite the "_all" suffix these
    //     are OFFICE-SCOPED for marketing. Reach is the union of `assignedOffices`
    //     (resolveOfficeScope), NOT company-wide. The suffix means "all leads in
    //     scope", and marketing's scope is its assigned offices.
    marketing: [
        'view_lead_inbox_all',
        'edit_leads_all',
        'assign_leads',
        'configure_lead_routing',
        'manage_lead_taxonomy',
        'view_broker_activity',
        'accept_invitation',
        'complete_own_registration',
        'access_conversations_inbox',
    ],
    // ── loyalty_client: separate app, no client-app caps ─────────────────────
    service_staff: ['view_lead_inbox_self', 'edit_leads_self', 'accept_invitation', 'complete_own_registration'],
    loyalty_client: [],
};
// ============================================================================
// Public API
// ============================================================================
/**
 * Returns true iff `role` is granted `cap` per the matrix.
 *
 * NO automatic founder bypass. Founder gets caps from the matrix only.
 * Per CLAUDE.md §7 founder has only `access_admin_portal`, so any client-app
 * cap check returns false for founder — by design, not by accident.
 *
 * Performance: O(N) array scan where N = caps for the role (max ~40). Plenty
 * fast for the auth hot path; if it ever becomes hot, swap in a precomputed
 * Set per role. Don't optimise pre-emptively.
 */
function hasCapability(role, cap) {
    if (!role)
        return false;
    // `granted` is per-row narrow due to `satisfies`. Cast to widen for
    // .includes(cap) — the invariant still holds because the matrix is
    // the only writer; this cast is read-only.
    const granted = exports.ROLE_CAPABILITIES[role];
    return granted ? granted.includes(cap) : false;
}
/**
 * OR-semantic: returns true iff `role` has ANY of `caps`.
 * Use for sites where the original `enforceRole([admin_role, broker_role])`
 * needs both an admin cap AND a broker-self cap, AND the inline self-scope
 * check then narrows the broker case (e.g. tagLoanIntroductionType,
 * activityAction). The cap is the perimeter, the inline `if` is the security.
 */
function hasAnyCapability(role, caps) {
    if (!role || caps.length === 0)
        return false;
    const granted = exports.ROLE_CAPABILITIES[role];
    if (!granted)
        return false;
    for (const c of caps) {
        if (granted.includes(c))
            return true;
    }
    return false;
}
// Variable declaration forces the type evaluation. `_` prefix marks unused.
// If the invariant breaks, this line errors with "Type 'never' is not
// assignable to type 'true'".
const _founderInvariantCheck = true;
void _founderInvariantCheck;
